import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { Address } from './address';
import { getCorrectSubscriptionIdFromAddress } from './address/utils';

export const GET_STRIPE_SUBSCRIPTION_DETAILS_QUERY = gql`
  query GetStripeSubscriptionDetails($addressId: String) {
    getStripeSubscriptionDetails(params: { addressId: $addressId }) {
      plan
      partnerProduct {
        id
        partnerProductId
        displayName
        platformSlaId
      }
      paused
      resumesAt
      balance
      customerId
      subscriptionStatus
      renewalDate
      renewalAmount
      billingFrequency
      cancelsAt
      trialEnd
      coupon {
        name
        amountOff
        duration
        durationInMonths
        percentOff
      }
      paymentMethod {
        type
        brand
        expMonth
        expYear
        last4
        bankName
      }
      schedule {
        cancelsAt
        phases {
          startDate
          endDate
          product
          phaseType
          priceInCents
          coupon
        }
      }
    }
  }
`;

export interface GetStripeSubscriptionDetailsQuery {
  getStripeSubscriptionDetails: GetStripeSubscriptionDetailsResponse;
}

export enum SubscriptionStatus {
  Active = 'Active',
  'Past Due' = 'Past Due',
  Unpaid = 'Unpaid',
  Canceled = 'Canceled',
  Incomplete = 'Incomplete',
  'Incomplete Expired' = 'Incomplete Expired',
  Trialing = 'Trialing',
}

export interface SubscriptionPartnerProduct {
  id?: string | null;
  partnerProductId: string;
  displayName: string;
  platformSlaId?: string | null;
}

export interface GetStripeSubscriptionDetailsResponse {
  plan: string;
  partnerProduct?: SubscriptionPartnerProduct | null;
  paused: boolean;
  balance: number;
  customerId: string;
  subscriptionStatus: string;
  coupon?: Coupon;
  renewalDate: string;
  renewalAmount: number | null;
  billingFrequency: string;
  paymentMethod?: PaymentMethod;
  cancelsAt?: string | null;
  trialEnd?: string | null;
  resumesAt?: string;
  schedule?: Schedule;
}

interface Coupon {
  name: string;
  amountOff: number | null;
  duration: string;
  durationInMonths: number;
  percentOff: number;
}

interface PaymentMethod {
  type: 'card' | 'us_bank_account';
  brand?: string;
  expMonth?: number;
  expYear?: number;
  last4?: string;
  bankName?: string;
}

export interface Schedule {
  cancelsAt: string | null;
  phases: SchedulePhase[];
}

export interface SchedulePhase {
  startDate: string;
  endDate: string;
  product?: string;
  phaseType: string;
  priceInCents?: number;
  coupon?: string;
}

export const useStripeSubscriptionDetails = ({ address }: { address?: Partial<Address> }) => {
  const addressSubscriptionId = useMemo(
    () => getCorrectSubscriptionIdFromAddress(address as Address),
    [address],
  );

  const { data, ...rest } = useQuery<GetStripeSubscriptionDetailsQuery>(
    GET_STRIPE_SUBSCRIPTION_DETAILS_QUERY,
    {
      variables: {
        addressId: address?.addressId,
      },
      skip: !address?.addressId || !addressSubscriptionId,
    },
  );

  return {
    stripeSubscriptionDetails: data?.getStripeSubscriptionDetails,
    ...rest,
  };
};
